import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, PageBody, PageHeader, StackedList } from "../ui/Field";

type Sess = { id: string; user_agent: string | null; ip: string | null; created_at: number; last_seen_at: number; expires_at: number };

export default function Sessions() {
  const [items, setItems] = useState<Sess[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.get<{ items: Sess[]; current: string | null }>("/api/account/sessions")
    .then((r) => { setItems(r.items); setCurrent(r.current); })
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, []);

  async function revoke(id: string) {
    if (!confirm("Revoke this session?")) return;
    try { await api.delete(`/api/account/sessions/${id}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ Account · Sessions"
        title={<>Devices <em>signed in</em></>}
        description="Each entry is a browser holding a valid session cookie. Revoke any you don't recognise."
      />
      {err && <Banner kind="error">{err}</Banner>}
      <Card bodyClassName="p-0">
        {items.length === 0 ? (
          <EmptyState title="No active sessions." />
        ) : (
          <StackedList className="px-5">
            {items.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13.5px] text-[var(--color-ink)] font-medium truncate max-w-md">{shortUA(s.user_agent)}</span>
                    {s.id === current && <Badge tone="success">Current</Badge>}
                  </div>
                  <div className="text-[11.5px] text-[var(--color-muted)] mt-0.5 font-mono">
                    {s.ip ?? "?"} · last seen {new Date(s.last_seen_at).toLocaleString()} · expires {new Date(s.expires_at).toLocaleDateString()}
                  </div>
                </div>
                {s.id !== current && <Button variant="ghost" size="sm" onClick={() => revoke(s.id)}>Revoke</Button>}
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}

function shortUA(ua: string | null): string {
  if (!ua) return "Unknown device";
  if (/Macintosh/.test(ua)) return "macOS · " + (ua.match(/Chrome\/|Safari\/|Firefox\//) ? ua.match(/(?:Chrome|Firefox|Safari)\/\d+/)?.[0] ?? "Safari" : "Safari");
  if (/Windows/.test(ua)) return "Windows · " + (ua.match(/(?:Chrome|Firefox|Edge)\/\d+/)?.[0] ?? "Browser");
  if (/iPhone/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android · " + (ua.match(/(?:Chrome|Firefox)\/\d+/)?.[0] ?? "Browser");
  return ua.slice(0, 60);
}
